import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Routes } from '../../utils/Routes';
import { ThingsData } from '../../utils/ThingsData';
import ArtPlaceholder from '../../assets/images/ArtPlaceholder.svg';

const useIntersectionObserver = (
  elementRef: React.RefObject<HTMLElement>,
  options = { threshold: 0.1, rootMargin: '50px' },
) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(element);
      }
    }, options);
    observer.observe(element);
    return () => observer.unobserve(element);
  }, [elementRef, options]);
  return isVisible;
};

const statusConfig: Record<string, { color: string; label: string }> = {
  sold: { color: 'bg-red-400', label: 'Sold' },
  available: { color: 'bg-emerald-400', label: 'Available' },
  'not for sale': { color: 'bg-sky-400', label: 'Not for sale' },
  'on hold': { color: 'bg-amber-400', label: 'On hold' },
  'inquire for price': { color: 'bg-amber-400', label: 'Inquire for price' },
};

const ThingCard = ({ thing }: { thing: (typeof ThingsData)[0] }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(cardRef);

  const handleImageLoad = useCallback(() => setImageLoaded(true), []);
  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(true);
  }, []);

  const statusDot = statusConfig[thing.metadata.status ?? ''] ?? null;

  return (
    <div
      ref={cardRef}
      id={`thing-${thing.metadata.id}`}
      className="break-inside-avoid mb-5"
    >
      <Link
        to={`${Routes.Things}/${thing.metadata.id}`}
        className="group block"
      >
        <div className="relative overflow-hidden rounded-sm shadow-sm hover:shadow-lg transition-shadow duration-500 cursor-pointer">
          {/* Placeholder skeleton */}
          {(!imageLoaded || imageError) && (
            <img src={ArtPlaceholder} alt="" className="w-full h-auto block" />
          )}

          {/* Lazy-loaded image */}
          {isVisible && !imageError && (
            <img
              src={thing.image}
              alt={thing.metadata.title}
              className={`w-full h-auto block transition-all duration-700 group-hover:scale-[1.025] ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              } ${imageLoaded ? '' : 'absolute inset-0'}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
          )}

          {/* Hover overlay — title + medium revealed on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex flex-col justify-end p-4 pointer-events-none">
            <h2 className="text-white text-xs font-light tracking-[0.18em] uppercase leading-snug">
              {thing.metadata.title || 'Untitled'}
            </h2>
            {thing.metadata.medium && (
              <p className="text-white/60 text-[10px] italic mt-0.5 leading-snug">
                {thing.metadata.medium}
              </p>
            )}
          </div>

          {/* Status dot — visible on hover */}
          {statusDot && (
            <span
              title={statusDot.label}
              className={`absolute top-3 right-3 w-2 h-2 rounded-full ${statusDot.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
            />
          )}
        </div>
      </Link>
    </div>
  );
};

export default function UnspokenThings() {
  const sortedThings = useMemo(() => {
    const unique = ThingsData.filter(
      (thing, i, self) =>
        i === self.findIndex((x) => x.metadata.id === thing.metadata.id),
    );

    return unique.sort((a, b) => {
      const aNum = parseInt(a.metadata.id);
      const bNum = parseInt(b.metadata.id);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return a.metadata.id.localeCompare(b.metadata.id);
    });
  }, []);

  const location = useLocation();

  useEffect(() => {
    const thingId = new URLSearchParams(location.search).get('id');
    if (thingId) {
      requestAnimationFrame(() => {
        document
          .getElementById(`thing-${thingId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.search]);

  return (
    <div className="container mx-auto mb-12 px-4 pt-4">
      {sortedThings.length > 0 ? (
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5">
          {sortedThings.map((thing) => (
            <ThingCard key={thing.metadata.id} thing={thing} />
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-400 mt-16 tracking-widest uppercase text-xs">
          Other Things Coming soon..
        </div>
      )}
    </div>
  );
}
